import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';

const TOOLS = { PEN: 'pen', ERASER: 'eraser' };

const COLORS = [
  { name: 'Black', value: '#1a1a1a' },
  { name: 'Blue', value: '#2563eb' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Green', value: '#16a34a' },
  { name: 'Purple', value: '#9333ea' },
];

const BOARD_BG = '#ffffff';

// ─── Always reapply DPR before drawing (survives ctx.restore()) ───────────────
const setDPR = (ctx) => {
  const dpr = window.devicePixelRatio || 1;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
};

// ─── Stroke renderer ──────────────────────────────────────────────────────────
const renderStroke = (ctx, points, color, lineWidth, isEraser) => {
  if (!points || points.length < 2) return;
  setDPR(ctx);
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (isEraser) {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.strokeStyle = 'rgba(0,0,0,1)';
    ctx.lineWidth = lineWidth * 8; // Wider eraser for whiteboard
  } else {
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    // Remove shadow blur for crisp whiteboard look
    ctx.shadowBlur = 0;
  }
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length - 1; i++) {
    const mx = (points[i].x + points[i + 1].x) / 2;
    const my = (points[i].y + points[i + 1].y) / 2;
    ctx.quadraticCurveTo(points[i].x, points[i].y, mx, my);
  }
  ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
  ctx.stroke();
  ctx.restore();
  setDPR(ctx);
};

// ─── Full board redraw ────────────────────────────────────────────────────────
const redrawAll = (canvas, strokes) => {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = BOARD_BG;
  ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
  strokes.forEach(s => renderStroke(ctx, s.points, s.color, s.lineWidth, s.tool === TOOLS.ERASER));
};

// ─── LiveKit sync hook ────────────────────────────────────────────────────────
const useBlackboardSync = (canvasRef, strokesRef, isLecturer, room) => {
  useEffect(() => {
    console.log('Blackboard Sync Hook: Room status:', room ? 'Connected' : 'Not Connected', 'isLecturer:', isLecturer);
  }, [room, isLecturer]);

  const publish = useCallback((payload, sids) => {
    if (!room) {
      console.warn('Blackboard: Cannot publish, room not ready');
      return;
    }
    try {
      const enc = new TextEncoder().encode(JSON.stringify(payload));
      console.log('Blackboard: Publishing', payload.type, sids ? `to ${sids.length} participants` : 'to all');
      room.localParticipant.publishData(enc, { reliable: true, ...(sids ? { destinationSids: sids } : {}) });
    } catch (err) {
      console.error('Blackboard Publish Error:', err);
    }
  }, [room]);

  const publishStroke = useCallback((s) => { if (isLecturer) publish({ type: 'BB_STROKE', stroke: s }); }, [isLecturer, publish]);
  const publishPoints = useCallback((pts) => { if (isLecturer) publish({ type: 'BB_POINTS', points: pts }); }, [isLecturer, publish]);
  const publishClear = useCallback(() => { if (isLecturer) publish({ type: 'BB_CLEAR' }); }, [isLecturer, publish]);
  const publishSync = useCallback((sid) => {
    if (!isLecturer) return;
    const payload = { type: 'BB_SYNC', strokes: strokesRef.current };
    console.log('Blackboard: Sending full sync', strokesRef.current.length, 'strokes');
    sid ? publish(payload, [sid]) : publish(payload);
  }, [isLecturer, publish, strokesRef]);

  useEffect(() => {
    if (!room) return;

    const handle = (payload, participant) => {
      try {
        const data = JSON.parse(new TextDecoder().decode(payload));
        const canvas = canvasRef.current;
        if (data.type === 'BB_STROKE') {
          strokesRef.current.push(data.stroke);
          if (canvas) redrawAll(canvas, strokesRef.current);
        } else if (data.type === 'BB_POINTS') {
          if (strokesRef.current.length > 0) {
            const last = strokesRef.current[strokesRef.current.length - 1];
            const oldLen = last.points.length;
            last.points.push(...data.points);
            if (canvas) renderStroke(canvas.getContext('2d'), last.points.slice(Math.max(0, oldLen - 2)), last.color, last.lineWidth, last.tool === TOOLS.ERASER);
          } else {
            publish({ type: 'BB_REQUEST_SYNC' });
          }
        } else if (data.type === 'BB_CLEAR') {
          strokesRef.current = [];
          if (canvas) redrawAll(canvas, []);
        } else if (data.type === 'BB_SYNC') {
          strokesRef.current = data.strokes || [];
          if (canvas) redrawAll(canvas, strokesRef.current);
        } else if (data.type === 'BB_REQUEST_SYNC' && isLecturer) {
          publishSync(participant?.sid);
        }
      } catch (err) {
        console.error('Blackboard Receive Error:', err);
      }
    };
    room.on('dataReceived', handle);
    if (!isLecturer) publish({ type: 'BB_REQUEST_SYNC' });
    return () => room.off('dataReceived', handle);
  }, [room, isLecturer, canvasRef, strokesRef, publish, publishSync]);

  return { publishStroke, publishPoints, publishClear, publishSync, room };
};

// ─── Toolbar ──────────────────────────────────────────────────────────────────
const Toolbar = ({ tool, setTool, color, setColor, lw, setLw, onClear, onUndo }) => (
  <div style={{
    flexShrink: 0,
    padding: '8px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
    backgroundColor: '#102a1c',
  }}>
    {/* Tools */}
    {[{ id: TOOLS.PEN, icon: '✏️' }, { id: TOOLS.ERASER, icon: '⬜' }].map(t => (
      <button key={t.id} onClick={() => setTool(t.id)} style={{
        padding: '5px 9px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14,
        background: tool === t.id ? 'rgba(110,231,183,0.2)' : 'rgba(255,255,255,0.05)',
        outline: tool === t.id ? '2px solid #6ee7b7' : '2px solid transparent',
      }}>{t.icon}</button>
    ))}

    {/* Colors */}
    {COLORS.map(c => (
      <button key={c.value} title={c.name} onClick={() => { setColor(c.value); setTool(TOOLS.PEN); }} style={{
        width: 18, height: 18, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer',
        backgroundColor: c.value,
        outline: color === c.value ? '2px solid #2563eb' : '2px solid transparent',
        outlineOffset: 2,
        transform: color === c.value ? 'scale(1.3)' : 'scale(1)',
        transition: 'transform 0.1s',
      }} />
    ))}

    {/* Widths */}
    {[2, 4, 8].map(w => (
      <button key={w} onClick={() => setLw(w)} style={{
        width: 28, height: 28, borderRadius: 7, border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: lw === w ? 'rgba(37,99,235,0.1)' : 'rgba(0,0,0,0.03)',
        outline: lw === w ? '2px solid #2563eb' : '2px solid transparent',
      }}>
        <div style={{ width: Math.min(w + 4, 18), height: w, backgroundColor: '#333', borderRadius: 99 }} />
      </button>
    ))}

    <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
      <button onClick={onUndo} style={{ padding: '5px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700, border: '1px solid rgba(0,0,0,0.1)', background: 'white', color: '#4b5563', cursor: 'pointer' }}>↩ Undo</button>
      <button onClick={onClear} style={{ padding: '5px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.05)', color: '#dc2626', cursor: 'pointer' }}>🗑 Clear</button>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const Blackboard = ({ isLecturer, onClose, isMobile, room }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const strokesRef = useRef([]);
  const isDrawing = useRef(false);
  const curStroke = useRef([]);

  const [tool, setTool] = useState(TOOLS.PEN);
  const [color, setColor] = useState(COLORS[0].value);
  const [lw, setLw] = useState(2);

  const { publishStroke, publishPoints, publishClear, publishSync } = useBlackboardSync(canvasRef, strokesRef, isLecturer, room);
  const pointBuffer = useRef([]);
  const lastPublish = useRef(0);

  // ── Canvas sizing: uses getBoundingClientRect AFTER layout ─────────────────
  const initCanvas = useCallback(() => {
    const canvas = containerRef.current;
    const cv = canvasRef.current;
    if (!canvas || !cv) return;

    requestAnimationFrame(() => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      cv.width = Math.round(rect.width * dpr);
      cv.height = Math.round(rect.height * dpr);
      cv.style.width = `${rect.width}px`;
      cv.style.height = `${rect.height}px`;

      redrawAll(cv, strokesRef.current);
    });
  }, []);

  useEffect(() => {
    initCanvas();
    const ro = new ResizeObserver(initCanvas);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [initCanvas]);

  // ── Precise coordinate: accounts for CSS scaling of canvas element ─────────
  const getCoords = useCallback((e) => {
    const cv = canvasRef.current;
    if (!cv) return { x: 0, y: 0 };
    const rect = cv.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : (e.changedTouches ? e.changedTouches[0] : e);

    const dpr = window.devicePixelRatio || 1;
    const scaleX = (cv.width / dpr) / rect.width;
    const scaleY = (cv.height / dpr) / rect.height;

    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top) * scaleY,
    };
  }, []);

  // ── Pointer handlers (unified mouse + touch) ───────────────────────────────
  const onPointerDown = useCallback((e) => {
    if (!isLecturer) return;
    e.preventDefault();
    isDrawing.current = true;
    const coords = getCoords(e);
    curStroke.current = [coords];

    // Start a new stroke record immediately
    const stroke = { points: [coords], color, lineWidth: lw, tool };
    strokesRef.current.push(stroke);
    publishStroke(stroke);

    canvasRef.current?.setPointerCapture?.(e.pointerId);
  }, [isLecturer, getCoords, color, lw, tool, publishStroke]);

  const onPointerMove = useCallback((e) => {
    if (!isDrawing.current || !isLecturer) return;
    e.preventDefault();
    const coords = getCoords(e);
    curStroke.current.push(coords);
    pointBuffer.current.push(coords);

    // Update the live record
    const last = strokesRef.current[strokesRef.current.length - 1];
    if (last) last.points = curStroke.current;

    // Real-time publish (throttled to 50ms)
    const now = Date.now();
    if (now - lastPublish.current > 50) {
      publishPoints(pointBuffer.current);
      pointBuffer.current = [];
      lastPublish.current = now;
    }

    const pts = curStroke.current;
    const len = pts.length;
    if (len < 2) return;

    const ctx = canvasRef.current.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // Apply scale

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (tool === TOOLS.ERASER) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = lw * 8;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
      ctx.lineWidth = lw;
    }

    ctx.beginPath();
    if (len === 2) {
      ctx.moveTo(pts[0].x, pts[0].y);
      ctx.lineTo(pts[1].x, pts[1].y);
    } else {
      const a = pts[len - 3], b = pts[len - 2], c = pts[len - 1];
      ctx.moveTo((a.x + b.x) / 2, (a.y + b.y) / 2);
      ctx.quadraticCurveTo(b.x, b.y, (b.x + c.x) / 2, (b.y + c.y) / 2);
    }
    ctx.stroke();
  }, [isLecturer, getCoords, tool, color, lw, publishPoints]);

  const onPointerUp = useCallback(() => {
    if (!isDrawing.current || !isLecturer) return;
    isDrawing.current = false;

    // Final publish for the remaining points
    if (pointBuffer.current.length > 0) {
      publishPoints(pointBuffer.current);
      pointBuffer.current = [];
    }

    curStroke.current = [];
  }, [isLecturer, publishPoints]);

  const handleClear = useCallback(() => {
    strokesRef.current = [];
    redrawAll(canvasRef.current, []);
    publishClear();
  }, [publishClear]);

  const handleUndo = useCallback(() => {
    if (!strokesRef.current.length) return;
    strokesRef.current.pop();
    redrawAll(canvasRef.current, strokesRef.current);
    publishSync(); // broadcast full state after undo
  }, [publishSync]);

  const panelWidth = isMobile ? 'calc(100% - 20px)' : '700px';

  return (
    <div style={{
      position: 'fixed',
      left: isMobile ? '10px' : 0,
      right: isMobile ? '10px' : 'auto',
      top: isMobile ? '70px' : 0,
      bottom: isMobile ? '70px' : 0,
      width: panelWidth,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f8fafc',
      borderRight: isMobile ? 'none' : '2px solid #000',
      border: isMobile ? '1px solid rgba(0,0,0,0.1)' : 'none',
      borderRadius: isMobile ? '20px' : 0,
      zIndex: 100001,
      boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        flexShrink: 0, padding: '10px 14px',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        backgroundColor: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>🖊️</span>
          <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 13 }}>Whiteboard</span>
          {!isLecturer && (
            <span style={{
              fontSize: 10, color: '#2563eb', background: 'rgba(37,99,235,0.05)',
              padding: '2px 7px', borderRadius: 99, border: '1px solid rgba(37,99,235,0.1)',
            }}>● Live View</span>
          )}
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: '0 4px' }}>×</button>
      </div>

      {/* Toolbar — lecturer only */}
      {isLecturer && (
        <Toolbar tool={tool} setTool={setTool} color={color} setColor={setColor}
          lw={lw} setLw={setLw} onClear={handleClear} onUndo={handleUndo} />
      )}

      {/* Canvas container fills remaining height */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#fff',
          cursor: !isLecturer ? 'default' : tool === TOOLS.ERASER ? 'cell' : 'crosshair',
          minHeight: 0,
        }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{
            display: 'block',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            // Canvas fills exactly its container
            position: 'absolute',
            top: 0, left: 0,
            width: '100%',
            height: '100%',
          }}
        />
        <div onClick={() => console.log('DEBUG: Room object:', room)} style={{ cursor: 'pointer' }}>
          {(!isLecturer && room) && (
            <div style={{
              position: 'absolute', bottom: 10, right: 10,
              background: 'rgba(255,255,255,0.9)', color: '#16a34a',
              fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 99,
              border: '1px solid rgba(22,163,74,0.2)',
              backdropFilter: 'blur(4px)',
              pointerEvents: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}>📡 Synchronized</div>
          )}
          {(isLecturer && room) && (
            <div style={{
              position: 'absolute', bottom: 10, right: 10,
              background: 'rgba(255,255,255,0.9)', color: '#2563eb',
              fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 99,
              border: '1px solid rgba(37,99,235,0.2)',
              backdropFilter: 'blur(4px)',
              pointerEvents: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}>📡 Live Syncing</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blackboard;
export { TOOLS };
